import * as React from 'react';

export interface IJsonViewProps {
  value: unknown;
  depth?: number;
  parentKey?: string;
  /** True when this value is followed by another sibling — render a trailing comma after the value's last character. */
  trailingComma?: boolean;
  /**
   * Keys whose array value collapses behind a `[…N items]` toggle. Applied only
   * to direct children of the payload root (depth 1) so a same-named array
   * nested deeper inside the payload still renders in full.
   */
  collapseArrayKeys?: ReadonlySet<string>;
  /**
   * Keys whose string value renders bold + bright-white inline (rather than
   * quoted + dimmed), so the caller can promote chosen prose fields to the
   * visual centerpiece. Applied at any depth.
   */
  prominentStringKeys?: ReadonlySet<string>;
}

interface ICollapsibleJsonArrayProps {
  length: number;
  /** Trailing comma to render after the collapsed summary (the expanded body carries its own). */
  comma: React.ReactNode;
  /** Fully-expanded array rendering, shown once the operator opens the node. */
  children: React.ReactNode;
}

/**
 * A JSON array that starts collapsed behind a `[…N items] ▸` toggle. Used for
 * arrays a caller names via `collapseArrayKeys` (e.g. a `toolResult` `content`
 * block that just duplicates its sibling `details`).
 */
const CollapsibleJsonArray = ({ length, comma, children }: ICollapsibleJsonArrayProps) => {
  const [collapsed, setCollapsed] = React.useState(true);
  if (collapsed) {
    return (
      <>
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="text-fg-quaternary hover:text-fg-secondary cursor-pointer"
        >
          [
          <span className="text-fg-tertiary">
            …{length} item{length === 1 ? '' : 's'}
          </span>
          ] ▸
        </button>
        {comma}
      </>
    );
  }
  return (
    <span>
      <button
        type="button"
        onClick={() => setCollapsed(true)}
        className="text-fg-quaternary hover:text-fg-secondary cursor-pointer"
      >
        ▾{' '}
      </button>
      {children}
    </span>
  );
};

/**
 * Tiny recursive JSON renderer. Keys are muted; primitives are colored by type.
 * The caller's container is expected to set `whitespace-pre-wrap` so embedded
 * newlines and the indentation this emits are preserved.
 *
 * Trailing commas are threaded through `trailingComma` rather than appended by
 * the parent — that way the comma always lands on the value's last visual line
 * (next to the closing `}` / `]`, or right after the primitive), never dangling
 * on a fresh line below a block-level child.
 *
 * Pass `collapseArrayKeys` to start named top-level arrays collapsed behind a
 * toggle, and `prominentStringKeys` to bold chosen prose fields (see
 * `IJsonViewProps`).
 */
const JsonView = ({
  value,
  depth = 0,
  parentKey,
  trailingComma = false,
  collapseArrayKeys,
  prominentStringKeys,
}: IJsonViewProps) => {
  const comma = trailingComma ? <span className="text-fg-quaternary">,</span> : null;

  if (value === null) {
    return (
      <>
        <span className="text-fg-error-primary">null</span>
        {comma}
      </>
    );
  }
  if (value === undefined) {
    return (
      <>
        <span className="text-fg-quaternary">undefined</span>
        {comma}
      </>
    );
  }
  if (typeof value === 'string') {
    if (parentKey !== undefined && (prominentStringKeys?.has(parentKey) ?? false)) {
      return (
        <>
          <span className="text-fg-primary font-semibold">{value}</span>
          {comma}
        </>
      );
    }
    return (
      <>
        <span className="text-fg-primary">&quot;{value}&quot;</span>
        {comma}
      </>
    );
  }
  if (typeof value === 'number') {
    return (
      <>
        <span className="text-fg-brand-primary">{value}</span>
        {comma}
      </>
    );
  }
  if (typeof value === 'boolean') {
    return (
      <>
        <span className="text-fg-warning-primary">{String(value)}</span>
        {comma}
      </>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <>
          <span className="text-fg-quaternary">[]</span>
          {comma}
        </>
      );
    }
    const indent = '  '.repeat(depth + 1);
    const closeIndent = '  '.repeat(depth);
    const body = (
      <span>
        <span className="text-fg-quaternary">[</span>
        {value.map((item, i) => (
          <span key={i} className="block">
            <span className="text-fg-quaternary">{indent}</span>
            <JsonView
              value={item}
              depth={depth + 1}
              trailingComma={i < value.length - 1}
              collapseArrayKeys={collapseArrayKeys}
              prominentStringKeys={prominentStringKeys}
            />
          </span>
        ))}
        <span className="block">
          <span className="text-fg-quaternary">{closeIndent}]</span>
          {comma}
        </span>
      </span>
    );
    // Only collapse a top-level payload key (depth 1) so a same-named array
    // nested deeper inside the payload still renders in full.
    if (depth === 1 && parentKey !== undefined && (collapseArrayKeys?.has(parentKey) ?? false)) {
      return (
        <CollapsibleJsonArray length={value.length} comma={comma}>
          {body}
        </CollapsibleJsonArray>
      );
    }
    return body;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return (
        <>
          <span className="text-fg-quaternary">{'{}'}</span>
          {comma}
        </>
      );
    }
    const indent = '  '.repeat(depth + 1);
    const closeIndent = '  '.repeat(depth);
    return (
      <span>
        <span className="text-fg-quaternary">{'{'}</span>
        {entries.map(([key, val], i) => (
          <span key={key} className="block">
            <span className="text-fg-quaternary">{indent}</span>
            <span className="text-fg-secondary">{key}</span>
            <span className="text-fg-quaternary">: </span>
            <JsonView
              value={val}
              depth={depth + 1}
              parentKey={key}
              trailingComma={i < entries.length - 1}
              collapseArrayKeys={collapseArrayKeys}
              prominentStringKeys={prominentStringKeys}
            />
          </span>
        ))}
        <span className="block">
          <span className="text-fg-quaternary">
            {closeIndent}
            {'}'}
          </span>
          {comma}
        </span>
      </span>
    );
  }
  // Fallback for any unhandled type (shouldn't happen for JSONB — `Object`
  // scalar values are always JSON-shaped). Stringify defensively so a
  // future scalar widening doesn't crash the renderer.
  return (
    <>
      <span className="text-fg-quaternary">{JSON.stringify(value)}</span>
      {comma}
    </>
  );
};

export default JsonView;
